import { logger } from '../utils';
import { CreateUserRequest, UpdateUserRequest } from '../types/user-management';

interface WSO2Role {
  audienceValue: string;
  display: string;
  audienceType: string;
  value: string;
  $ref: string;
  audienceDisplay: string;
}

interface WSO2User {
  id: string;
  userName: string;
  schemas: string[];
  name?: {
    givenName?: string;
    familyName?: string;
  };
  emails?: { value: string; type?: string }[];
  roles?: WSO2Role[];
  groups?: string[];
  meta?: {
    created?: string;
    lastModified?: string;
    location?: string;
    resourceType?: string;
  };
  "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User"?: {
    organization?: string;
    manager?: {
      value: string; // partnerId for students
    };
  };
}

class WSO2Service {
  private usersEndpoint: string;
  private appId: string;

  constructor() {
    this.usersEndpoint = import.meta.env.VITE_WSO2_SCIM2_Users_API;
    this.appId = import.meta.env.VITE_WSO2_APP_ID;
    logger.debug('WSO2', 'Service initialized', { 
      usersEndpoint: this.usersEndpoint,
      appId: this.appId
    });
  }

  private async fetchWithAuth(token: string, path: string, options: RequestInit = {}) {
    const url = path ? `${this.usersEndpoint}/${path}` : this.usersEndpoint;
    const headers = {
      ...options.headers,
      Authorization: token, // Token already has Bearer prefix
      'Content-Type': 'application/scim+json',
      Accept: 'application/scim+json',
    };

    logger.debug('WSO2', 'Making authenticated request', { 
      url, 
      method: options.method,
      headers: {
        ...headers,
        Authorization: token.substring(0, 20) + '...' // Log partial token for debugging
      }
    });

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to parse error response' }));
        logger.error('WSO2', 'API request failed', { 
          url,
          status: response.status,
          statusText: response.statusText,
          error,
          headers: Object.fromEntries(response.headers.entries())
        });
        throw new Error(error.detail || error.error_description || error.error || error.message || 'Request failed');
      }

      const data = await response.json();
      logger.debug('WSO2', 'API request successful', { 
        url, 
        status: response.status,
        response: data
      });

      return data;
    } catch (error) {
      logger.error('WSO2', 'API request error', { 
        url,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }

  private mapUserRole(user: WSO2User): 'tedy-admin' | 'tedy-partner' | 'tedy-student' {
    try {
      // Filter roles to only include those for our application
      const appRoles = user.roles?.filter(role => 
        role.audienceType === 'application' && 
        role.audienceValue === this.appId
      ) || [];

      logger.debug('WSO2', 'Filtered application roles', {
        userName: user.userName,
        appId: this.appId,
        appRoles
      });

      // Check for tedy-specific roles
      for (const role of appRoles) {
        const roleDisplay = role.display.toLowerCase();
        if (roleDisplay === 'tedy-admin') return 'tedy-admin';
        if (roleDisplay === 'tedy-partner') return 'tedy-partner';
        if (roleDisplay === 'tedy-student') return 'tedy-student';
      }

      // Default to student if no matching role found
      logger.debug('WSO2', 'No matching application role found', {
        userName: user.userName,
        appId: this.appId,
        appRoles
      });
      return 'tedy-student';
    } catch (error) {
      logger.error('WSO2', 'Error mapping user role', {
        error,
        user: {
          userName: user.userName,
          roles: user.roles
        }
      });
      return 'tedy-student';
    }
  }

  async listUsers(token: string) {
    try {
      logger.info('WSO2', 'Fetching users list');
      const response = await this.fetchWithAuth(token, '', {
        method: 'GET',
      });

      if (!response.Resources || !Array.isArray(response.Resources)) {
        logger.error('WSO2', 'Invalid response format', { response });
        throw new Error('Invalid response format: missing Resources array');
      }

      const users = response.Resources.map((user: WSO2User) => {
        try {
          const mappedUser = {
            id: user.id,
            email: user.emails?.[0]?.value || user.userName,
            name: `${user.name?.givenName || ''} ${user.name?.familyName || ''}`.trim() || user.userName,
            role: this.mapUserRole(user),
            organization: user["urn:ietf:params:scim:schemas:extension:enterprise:2.0:User"]?.organization,
            partnerId: user["urn:ietf:params:scim:schemas:extension:enterprise:2.0:User"]?.manager?.value,
            createdAt: user.meta?.created || new Date().toISOString(),
            updatedAt: user.meta?.lastModified || new Date().toISOString(),
          };

          logger.debug('WSO2', 'Mapped user', {
            original: {
              id: user.id,
              userName: user.userName,
              roles: user.roles,
              groups: user.groups
            },
            mapped: mappedUser
          });

          return mappedUser;
        } catch (error) {
          logger.error('WSO2', 'Failed to map user', {
            error,
            user: {
              id: user.id,
              userName: user.userName
            }
          });
          throw error;
        }
      });

      logger.debug('WSO2', 'Users list fetched', { count: users.length });
      return users;
    } catch (error) {
      logger.error('WSO2', 'Failed to list users', { error });
      throw error;
    }
  }

  async createUser(token: string, data: CreateUserRequest) {
    try {
      logger.info('WSO2', 'Creating new user', { email: data.email, role: data.role });
      const [firstName, ...restName] = data.name.split(' ');
      const user: Partial<WSO2User> = {
        schemas: [
          "urn:ietf:params:scim:schemas:core:2.0:User",
          "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User"
        ],
        userName: data.email,
        name: {
          givenName: firstName,
          familyName: restName.join(' '),
        },
        emails: [{ value: data.email }],
        roles: [
          {
            audienceValue: this.appId,
            display: data.role,
            audienceType: 'application',
            value: crypto.randomUUID(),
            $ref: `${this.usersEndpoint}/v2/Roles/${crypto.randomUUID()}`,
            audienceDisplay: 'TalentED YYC'
          }
        ],
        "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User": {
          organization: data.organization,
          ...(data.partnerId && {
            manager: {
              value: data.partnerId,
            },
          }),
        },
      };

      const response = await this.fetchWithAuth(token, '', {
        method: 'POST',
        body: JSON.stringify(user),
      });

      return {
        id: response.id,
        email: response.emails[0].value,
        name: `${response.name.givenName} ${response.name.familyName || ''}`.trim(),
        role: this.mapUserRole(response),
        organization: response["urn:ietf:params:scim:schemas:extension:enterprise:2.0:User"]?.organization,
        partnerId: response["urn:ietf:params:scim:schemas:extension:enterprise:2.0:User"]?.manager?.value,
        createdAt: response.meta?.created || new Date().toISOString(),
        updatedAt: response.meta?.lastModified || new Date().toISOString(),
      };
    } catch (error) {
      logger.error('WSO2', 'Failed to create user', { error });
      throw error;
    }
  }

  async updateUser(token: string, userId: string, data: UpdateUserRequest) {
    try {
      logger.info('WSO2', 'Updating user', { userId });
      const [firstName, ...restName] = data.name?.split(' ') || [];
      const updateData: Partial<WSO2User> = {
        schemas: [
          "urn:ietf:params:scim:schemas:core:2.0:User",
          "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User"
        ],
        name: firstName ? {
          givenName: firstName,
          familyName: restName.join(' '),
        } : undefined,
        "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User": {
          organization: data.organization,
          ...(data.partnerId && {
            manager: {
              value: data.partnerId,
            },
          }),
        },
      };

      const response = await this.fetchWithAuth(token, userId, {
        method: 'PATCH',
        body: JSON.stringify({
          schemas: ["urn:ietf:params:scim:api:messages:2.0:PatchOp"],
          Operations: [{
            op: "replace",
            value: updateData,
          }],
        }),
      });

      return {
        id: response.id,
        email: response.emails[0].value,
        name: `${response.name.givenName} ${response.name.familyName || ''}`.trim(),
        role: this.mapUserRole(response),
        organization: response["urn:ietf:params:scim:schemas:extension:enterprise:2.0:User"]?.organization,
        partnerId: response["urn:ietf:params:scim:schemas:extension:enterprise:2.0:User"]?.manager?.value,
        createdAt: response.meta?.created || new Date().toISOString(),
        updatedAt: response.meta?.lastModified || new Date().toISOString(),
      };
    } catch (error) {
      logger.error('WSO2', 'Failed to update user', { error });
      throw error;
    }
  }

  async deleteUser(token: string, userId: string) {
    try {
      logger.info('WSO2', 'Deleting user', { userId });
      await this.fetchWithAuth(token, userId, {
        method: 'DELETE',
      });
    } catch (error) {
      logger.error('WSO2', 'Failed to delete user', { error });
      throw error;
    }
  }
}

export const wso2Service = new WSO2Service();
